import type { ServiceId } from './services';

export type ActId = 'introduction' | 'signals' | 'connections' | 'intelligence';

export interface StoryAct {
  id: ActId;
  /** Service this act brings to life, if any. */
  service?: ServiceId;
  /**
   * Whether this act is the one a service links to. The act then takes the
   * service id as its anchor and lists the service's features under the copy.
   */
  showServiceDetail?: boolean;
  eyebrow: string;
  headline: string;
  body: string;
}

export const heroContent = {
  title: 'AI that works for',
  titleGradient: ' Retail & CPG',
};

/** Anchor used for an act: the service id when the act presents that service. */
export const actAnchor = (act: StoryAct) =>
  act.showServiceDetail && act.service ? act.service : `act-${act.id}`;

export const storyActs: StoryAct[] = [
  {
    id: 'introduction',
    service: 'data-foundations',
    eyebrow: 'Intelligence for Retail & CPG',
    headline: 'Every product has a bigger story.',
    body: 'The signals to understand performance are already there. We help you connect them.',
  },
  {
    id: 'signals',
    service: 'data-foundations',
    showServiceDetail: true,
    eyebrow: 'Data Foundations',
    headline: 'Start with data you can trust.',
    body: 'We build the data platforms, pipelines, and models that turn fragmented information into a reliable foundation for better decisions.',
  },
  {
    id: 'connections',
    service: 'integrations',
    showServiceDetail: true,
    eyebrow: 'Integrations',
    headline: 'Connect the systems. Reveal the relationships.',
    body: 'We integrate the platforms your teams already use, connecting data across products, channels, customers, and operations.',
  },
  {
    id: 'intelligence',
    service: 'applied-ai',
    showServiceDetail: true,
    eyebrow: 'Applied AI',
    headline: 'Turn connected data into business impact.',
    body: 'We identify, build, and deploy AI solutions that help Retail and CPG teams forecast demand, optimize pricing and assortment, personalize experiences, and make better decisions.',
  },
];

/** Labels that surface in the scene as the story unfolds. */
export const sceneLabels = {
  /** Act II: the signal clusters around the bottle. */
  signals: ['Product', 'Customer', 'Inventory', 'Sales'] as const,
  /** Act III: the systems the clusters connect out to. */
  systems: ['Commerce', 'Marketing', 'POS', 'ERP', 'Planning'] as const,
};
